'use client';

import { useEffect } from 'react';
import { sendDataLayerEvent } from '@utils/send-data-layer-event/send-data-layer-event';
import { GA_JOB_APPLY_COOKIE_NAME, MAX_DECODE_ITERATIONS } from '@utils/analytics/ga-cookie-helper';

/**
 * @description Fires a GA4 job_apply_event via dataLayer once if a short-lived cookie flag exists, then deletes the cookie.
 */
export const useGaJobApplyOnce = (cookieName = GA_JOB_APPLY_COOKIE_NAME) => {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const raw = document.cookie.split('; ').find((row) => row.startsWith(`${cookieName}=`));

    if (!raw) return;

    try {
      let decoded = raw.split('=')[1] || '';

      // Decode up to MAX_DECODE_ITERATIONS times or until the delimiter ';' is present,
      // which indicates the string is ready for parsing.
      for (let i = 0; i < MAX_DECODE_ITERATIONS; i += 1) {
        if (decoded.includes(';')) break;
        if (/%[0-9A-Fa-f]{2}/.test(decoded)) {
          try {
            decoded = decodeURIComponent(decoded);
          } catch {
            break;
          }
        } else {
          break;
        }
      }

      // Expected format: itemId;itemName;itemBrand;placement;itemListId
      const parts = decoded.split(';');

      if (parts.length < 5) {
        // Malformed or incomplete cookie; do not send event
        return;
      }

      const [itemId, itemName, itemBrand, placement, itemListId] = parts;
      sendDataLayerEvent({
        event: 'job_apply_event',
        googleMeasurementEvents: {
          job_apply: {
            item_id: itemId,
            item_name: itemName,
            item_brand: itemBrand,
            placement,
            item_list_id: itemListId,
          },
        },
      });
    } catch (error) {
      //
    } finally {
      // Delete cookie to avoid duplicate firing on refresh/back
      document.cookie = `${cookieName}=; Max-Age=0; path=/`;
    }
  }, [cookieName]);
};
